import * as React from "react";
import {
  KeyValue,
  Metadata,
  Query,
  SearchPillData,
  SimpleTableRow,
  SortProperties,
  TableHeader,
} from "../../../interfaces";
import {
  apiPost,
  buildFilterString,
  buildSearchString,
  fetchData,
} from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import MultiHeaderTable from "../../MultiHeaderTable/MultiHeaderTable";
import SimpleTableBody from "../../MultiHeaderTable/SimpleTableBody";
import ResultPagination from "../../../containers/ResultPagination";
import { Divider, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { chunkArray, fileNameFormat } from "../../../common/services/Utilities";
import ApiDataDownload, {
  DownloadHeaders,
  apiDownloadProps,
} from "../../ApiDataDownload";
import SearchPills from "../../../containers/SearchPills";
import { assetSizeLookup, summaryTableHeader } from "./SummaryTableUtilities";
import ModalDialog from "../../ModalDialog";
import BranchOfficeDeposits from "../../../containers/BranchOfficeDeposits";
import ShowHideButton from "../../ShowHideButton";

interface Props {
  appState: Query;
  sortProperties: SortProperties;
  updateSort: (sortField: string, sortDirection: string) => void;
  updatePagination: (pageNumber: number, resultLimit: number) => void;
}

const useStyles = makeStyles(theme => ({
  heading: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 26,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },
}));

export default function SummaryLevelTwo({
  appState,
  sortProperties,
  updateSort,
  updatePagination,
}: Props) {
  const classes = useStyles();
  const charterLevelTwoAppstate = React.useMemo(() => {
    return {
      filters: appState.filters,
      search: appState.search,
      ui: appState.ui,
      sort: appState.sort,
      pagination: appState.pagination,
    };
  }, [
    appState.filters,
    appState.pagination,
    appState.search,
    appState.sort,
    appState.ui,
  ]);

  const instTypeParam: keyof InstTypeLookup =
    appState.ui.reportType === "nationalTimeSeries"
      ? appState.ui.institutionTypeTimeSeries
      : appState.ui.instType || "";
  const assetSizeParam: keyof AssetSizeFilterLookup =
    appState.ui.assetSize || "";
  let summaryType = appState.ui.summaryTableType
    ? appState.ui.summaryTableType.replace(/\*/g, "")
    : appState.ui.summaryTableType;
  summaryType =
    appState.ui.summaryTableType === "nationalTimeSeries"
      ? instTypeParam
      : summaryType;
  const [previousAppState, setPreviousAppState] = React.useState(
    charterLevelTwoAppstate,
  );
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [summaryData, setSummaryData] = React.useState<SimpleTableRow[]>([]);
  const [aggregatedData, setAggregatedData] = React.useState<SimpleTableRow[]>(
    [],
  );
  const [fetchAgg, setFetchAgg] = React.useState(false);
  const [certs, setCerts] = React.useState([]);
  const [open, setOpen] = React.useState("");
  const [name, setName] = React.useState("");
  const branchOfficeModal = (
    <ModalDialog
      title={name}
      display={open !== ""}
      setDisplay={() => setOpen("")}
      maxWidth="lg"
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...appState,
          filters: { ...appState.filters, cert: open },
          ui: { ...appState.ui, institutionType: "banks" },
        }}
      />
    </ModalDialog>
  );

  const [metaData, setMetaData] = React.useState<Metadata>({
    count: 0,
  });

  const transformDataToTableBodyData = (tableData: KeyValue[]) => {
    return tableData.map((data: KeyValue) => {
      const tableCellData: SimpleTableRow | KeyValue = {};
      headerMetaData.forEach((cell: KeyValue) => {
        tableCellData[cell.title] = {
          value:
            cell.title === "Total Deposits (Thousands USD)"
              ? data["sum_DEPSUMBR"]?.toLocaleString()
              : cell.title === "Total Assets (Thousands USD)"
              ? data["sum_ASSET"]?.toLocaleString()
              : cell.title === "Number of Offices"
              ? data["count"]
              : data[cell.apiField],
          styles: cell.styles,
        };
      });
      tableCellData.rowOnClick = (e: any) => {
        if (
          (e.key === "Enter" || e.nativeEvent.type === "click") &&
          appState.filters.lastYear &&
          `${tableCellData.CERT.value}`
        ) {
          setOpen(`${tableCellData.CERT.value}`);
          setName(`${tableCellData["Institution Name"].value}`);
        }
      };
      return tableCellData;
    });
  };
  const classLookup: KeyValue = {
    all: "All Institutions",
    CB: "Commercial Banks",
    SA: "Savings Institutions",
    IB: "U.S. Branches of Foreign Banks",
    hqInState: "Headquartered In State",
    hqOutOfState: "Headquartered Out Of State",
  };

  const summaryTableTypeLookup: KeyValue = React.useMemo(() => {
    return {
      "Commercial Banks": " AND INSURED:CB",
      "Savings Institutions": " AND INSURED:SA",
      "U.S. Branches of Foreign Banks": " AND INSURED:IB",
      "National Charter": " AND INSURED:CB AND CHARTER:FED",
      "State Charter": " AND INSURED:CB AND CHARTER:STATE",
      "Federal Charter Savings Associations": " AND INSURED:SA AND CHARTER:FED",
      "State Charter Savings Institutions": " AND INSURED:SA AND CHARTER:STATE",
      "Federal Reserve Member":
        " AND INSURED:CB AND BKCLASS:SM AND CHARTER:STATE",
      "Federal Reserve Nonmember":
        " AND INSURED:CB AND BKCLASS:NM AND CHARTER:STATE",
      "FDIC-Supervised Savings Banks": " AND INSURED:SA AND REGAGNT:FDIC",
      "Federal Reserve-Supervised": " AND INSURED:SA AND REGAGNT:FED",
      "OTS-Supervised Savings Associations":
        " AND INSURED:SA AND REGAGNT:FED,OTS,OCC AND CHARTER:STATE",
    };
  }, []);
  interface InstTypeLookup {
    CB: string;
    SA: string;
    IB: string;
    all: string;
  }
  const instTypeLookup: InstTypeLookup = {
    CB: "INSURED:CB",
    SA: "INSURED:SA",
    IB: "INSURED:IB",
    all: "",
  };
  const countyInstType = instTypeLookup[instTypeParam]
    ? ` AND ${instTypeLookup[instTypeParam]}`
    : "";
  interface AssetSizeFilterLookup {
    upTo25Million: string;
    upTo50Million: string;
    upTo100Million: string;
    upTo300Million: string;
    upTo500Million: string;
    upTo1Billion: string;
    upTo3Billion: string;
    upTo10Billion: string;
    over10Billion: string;
  }
  const assetSizeFilterLookup: AssetSizeFilterLookup = {
    upTo25Million: "ASSET:[0 TO 25000]",
    upTo50Million: "ASSET:[25001 TO 50000]",
    upTo100Million: "ASSET:[50001 TO 100000]",
    upTo300Million: "ASSET:[100001 TO 300000]",
    upTo500Million: "ASSET:[300001 TO 500000]",
    upTo1Billion: "ASSET:[500001 TO 1000000]",
    upTo3Billion: "ASSET:[1000001 TO 3000000]",
    upTo10Billion: "ASSET:[3000001 TO 10000000]",
    over10Billion: "ASSET:>10000000",
  };

  const combinedAssetSizeFilters =
    appState.ui.reportType !== "assetSize"
      ? ""
      : instTypeParam && instTypeParam !== "all"
      ? ` AND ${[
          instTypeLookup[instTypeParam],
          assetSizeFilterLookup[assetSizeParam],
        ]
          .filter(val => val)
          .join(" AND ")}`
      : ` AND ${assetSizeFilterLookup[assetSizeParam]}`;

  const stateFilter = React.useMemo(() => {
    if (
      appState.filters.stalpbr &&
      appState.ui.reportType === "stateAndTerritories"
    ) {
      return ` AND STALPBR:"${appState.filters.stalpbr}"&get_unique=CERT`;
    } else if (
      appState.filters.county &&
      appState.ui.reportType === "stateCounty"
    ) {
      return ` AND CNTYNAMB:"${appState.filters.county}" AND STALPBR:"${appState.filters.stalpbr}"&get_unique=CERT`;
    } else if (
      appState.filters.stalpbr &&
      appState.ui.reportType === "stateCounty"
    ) {
      return ` AND STALPBR:"${appState.filters.stalpbr}"&get_unique=CERT`;
    } else if (
      appState.filters.stalpbr &&
      appState.ui.reportType === "nationalTimeSeries"
    ) {
      return ` AND STALPBR:"${appState.filters.stalpbr}"&get_unique=CERT`;
    } else if (appState.ui.reportType === "charterClass") {
      return " AND BKMO:1";
    } else {
      return "&get_unique=CERT";
    }
  }, [
    appState.filters.county,
    appState.filters.stalpbr,
    appState.ui.reportType,
  ]);

  const stateCharterFilter = React.useMemo(() => {
    const instType = appState.ui.instType;
    if (appState.ui.reportType === "stateCharterClass") {
      const filterLookup: KeyValue = {
        hqInState: ` AND STALP:"${appState.filters.stalpbr}" AND STALPBR:"${appState.filters.stalpbr}"`,
        hqOutOfState: ` AND !STALP:"${appState.filters.stalpbr}" AND STALPBR:"${appState.filters.stalpbr}"`,
        all: ` AND STALPBR:"${appState.filters.stalpbr}"`,
      };
      return filterLookup[instType] ? filterLookup[instType] : "";
    }
    return "";
  }, [appState.filters.stalpbr, appState.ui.instType, appState.ui.reportType]);

  const msaFilter =
    appState.filters.msa && appState.ui.reportType === "metroStatisticalArea"
      ? ` AND MSANAMB:"${appState.filters.msa}"`
      : "";

  const downloadHeaders: DownloadHeaders[] = [
    { label: "Report Period", key: "YEAR" },
    appState.ui.reportType === "stateCounty"
      ? { label: "State", key: "STALPBR" }
      : { label: "", key: "" },
    appState.ui.reportType === "stateCounty"
      ? { label: "County", key: "CNTYNAMB" }
      : { label: "", key: "" },
    { label: "Bank Class", key: "BKCLASS" },
    { label: "Institution Name", key: "NAMEFULL" },
    { label: "Cert", key: "CERT" },
    { label: "Main Office Location", key: "STNAME" },
    { label: "Number of Offices", key: "count" },
    appState.ui.reportType === "assetSize"
      ? {
          label: "Total Assets (Thousands USD)",
          key: "sum_ASSET",
        }
      : { label: "", key: "" },
    { label: "Total Deposits (Thousands USD)", key: "sum_DEPSUMBR" },
  ].filter((header: KeyValue) => header.label);

  const downloadFileName = () => {
    let fileName: string = "";
    if (
      appState.ui.reportType === "assetSize" &&
      appState.ui.instType &&
      appState.ui.assetSize
    ) {
      fileName = `${fileNameFormat(
        `Summary ${classLookup[appState.ui.instType].replace(
          " ",
          "_",
        )} ${assetSizeLookup[appState.ui.assetSize].replace(
          " ",
          "_",
        )}_formatted`,
      )}.csv`;
    } else if (
      (appState.ui.reportType === "stateAndTerritories" ||
        appState.ui.reportType === "stateCharterClass") &&
      appState.ui.instType &&
      appState.filters.stalpbr
    ) {
      fileName = `${fileNameFormat(
        `Summary ${classLookup[appState.ui.instType].replace(
          " ",
          "_",
        )} ${appState.filters.stalpbr.replace(" ", "_")}_formatted`,
      )}.csv`;
    } else if (
      appState.ui.reportType === "stateCounty" &&
      appState.ui.instType &&
      (appState.filters.stname || appState.filters.stalpbr)
    ) {
      fileName = `${fileNameFormat(
        `Summary State by County ${
          classLookup[appState.ui.instType]
        } formatted`,
      )}.csv`;
    } else if (
      appState.ui.reportType === "nationalTimeSeries" &&
      appState.filters.stalpbr &&
      appState.ui.institutionTypeTimeSeries
    ) {
      fileName = `${fileNameFormat(
        `Summary_Tables_Time_Series_${appState.ui.institutionTypeTimeSeries.replace(
          " ",
          "_",
        )} ${appState.filters.stalpbr.replace(" ", "_")}_formatted`,
      )}.csv`;
    } else if (
      appState.ui.reportType === "metroStatisticalArea" &&
      appState.ui.instType &&
      appState.filters.msa
    ) {
      fileName = `${fileNameFormat(
        `Summary_Tables_MSA ${classLookup[appState.ui.instType].replace(
          " ",
          "_",
        )} ${appState.filters.msa.replace(",", "")}_formatted`,
      )}.csv`;
    } else if (
      appState.ui.reportType === "charterClass" &&
      appState.ui.summaryTableType
    ) {
      fileName = `${fileNameFormat(
        `Summary ${appState.ui.summaryTableType.replace(
          " ",
          "_",
        )} ${appState.filters.stalpbr.replace(" ", "_")}_formatted`,
      )}.csv`;
    } else {
      fileName = `${fileNameFormat("Summary_Level_2_formatted")}.csv`;
    }
    return fileName;
  };

  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat("Summary_Level_2_raw")}.csv`,
      apiPath: "sod",
      text: "Download Results Data - raw",
      downloadUrl: `${ApiBaseUrl()}/sod?filters=YEAR:${
        appState.filters.lastYear
      }${countyInstType}${msaFilter}${summaryTableTypeLookup[summaryType] ||
        ""}${combinedAssetSizeFilters}${stateCharterFilter}${stateFilter}&sort_by=${
        appState.sort.sortField
      }&sort_order=${appState.sort.sortOrder}`,
    },
    {
      appState,
      fileName: downloadFileName(),
      apiPath: "sod",
      text: "Download Results Data - formatted",
      headers: downloadHeaders,
    },
  ];

  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label:
        appState.ui.reportType === "nationalTimeSeries"
          ? ""
          : "Reporting Period",
      value:
        appState.ui.reportType === "nationalTimeSeries"
          ? `${appState.filters.lastYear}`
          : `06/30/${appState.filters.lastYear}`,
      search: false,
      static: true,
      formKey: "lastYear",
    },
    {
      label: "Assets",
      value:
        appState.ui.reportType === "assetSize"
          ? assetSizeLookup[appState.ui.assetSize]
          : "",
      search: false,
      static: true,
      formKey: "assets",
    },
    {
      label: "",
      value:
        appState.ui.reportType === "assetSize" ||
        appState.ui.reportType === "stateAndTerritories" ||
        appState.ui.reportType === "stateCharterClass" ||
        appState.ui.reportType === "stateCounty" ||
        appState.ui.reportType === "metroStatisticalArea"
          ? classLookup[appState.ui.instType]
          : "",
      search: false,
      static: true,
      formKey: "instType",
    },
    {
      label: "",
      value:
        appState.ui.reportType === "nationalTimeSeries"
          ? appState.ui.institutionTypeTimeSeries
          : "",
      search: false,
      static: true,
      formKey: "institutionTypeTimeSeries",
    },
    {
      label: "State",
      value: appState.filters.stname,
      search: false,
      static: true,
      formKey: "stname",
    },
    {
      label: "State",
      value: appState.filters.stalpbr,
      search: false,
      static: true,
      formKey: "stalpbr",
    },
    {
      value:
        appState.ui.reportType === "charterClass" ||
        appState.ui.reportType === "stateCharterClass"
          ? summaryType
          : "",
      search: false,
      static: true,
      formKey: "summaryTableType",
    },
    {
      label: "County",
      value: `${appState.filters.county}`,
      search: false,
      static: false,
      formKey: "county",
    },
    {
      label: "MSA",
      value:
        appState.ui.reportType === "metroStatisticalArea"
          ? `${appState.filters.msa}`
          : "",
      search: false,
      static: true,
      formKey: "msa",
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={summaryTablesSearchPills} />}
    />
  );

  const rowsPerPage = appState.pagination.resultLimit
    ? appState.pagination.resultLimit
    : "25";

  const page = appState.pagination.pageNumber
    ? appState.pagination.pageNumber
    : 1;

  const totalAssets =
    appState.ui.reportType === "assetSize"
      ? {
          title: "Total Assets (Thousands USD)",
          apiField: "",
          styles: { textAlign: "right", width: 120 },
        }
      : {};
  const headerMetaData: KeyValue[] = [
    {
      title: "Institution Name",
      apiField: "NAMEFULL",
      styles: { textAlign: "left" },
    },
    {
      title: "CERT",
      apiField: "CERT",
      styles: { textAlign: "center", width: 120 },
    },
    {
      title: "Main Office Location",
      apiField: "STNAME",
      styles: { textAlign: "center", width: 120 },
    },
    {
      title: "Number of Offices",
      apiField: "",
      styles: { textAlign: "center", width: 120 },
    },
    totalAssets,
    {
      title: "Total Deposits (Thousands USD)",
      apiField: "",
      styles: { textAlign: "right", width: 120 },
    },
  ].filter((header: KeyValue) => header.title);

  const tableHeaders: TableHeader[][] = [
    headerMetaData.map(element => ({
      title: element.title,
      styles: element.styles,
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];
  const tableBody = (
    <SimpleTableBody tableData={transformDataToTableBodyData(aggregatedData)} />
  );
  const [makeDownloadReq, setMakeDownloadReq] = React.useState(true);
  const [downloadData, setDownloadData] = React.useState([]);
  const [downloadCerts, setDownloadCerts] = React.useState<number[][]>([]);
  const [fetchAggDownload, setFetchAggDownload] = React.useState(false);
  React.useEffect(() => {
    if (
      (JSON.stringify({
        filters: charterLevelTwoAppstate.filters,
        search: charterLevelTwoAppstate.search,
        sort: charterLevelTwoAppstate.sort,
      }) !==
        JSON.stringify({
          filters: previousAppState.filters,
          search: previousAppState.search,
          sort: previousAppState.sort,
        }) ||
        makeDownloadReq) &&
      metaData.count
    ) {
      setMakeDownloadReq(false);
      const numDownloads = Math.ceil(metaData.count / 10000);
      let isMounted = true;
      const apiRequests: any = [
        [...Array(numDownloads)].map(
          (val, int) =>
            `${ApiBaseUrl()}/sod?filters=YEAR:${
              appState.filters.lastYear
            }${countyInstType}${summaryTableTypeLookup[summaryType] ||
              ""}${combinedAssetSizeFilters}${stateCharterFilter}${msaFilter}${stateFilter}&limit=${10000}&offset=${int *
              10000}&sort_by=${appState.sort.sortField}&sort_order=${
              appState.sort.sortOrder
            }`,
        ),
      ];
      const concatRequests = [].concat
        .apply([], apiRequests)
        .map(url => fetchData(url));
      (async () => {
        await Promise.all(concatRequests)
          .then(response => {
            if (isMounted) {
              const data: any = response.map(res => res.data);
              setDownloadData([].concat.apply([], data));
              const certs = response.map(res =>
                res.data.map((data: KeyValue) => data.CERT),
              );
              setDownloadCerts(certs);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setFetchAggDownload(true);
          });
      })();
    }
  }, [
    appState.filters.lastYear,
    appState.sort.sortField,
    appState.sort.sortOrder,
    charterLevelTwoAppstate,
    combinedAssetSizeFilters,
    countyInstType,
    downloadData.length,
    makeDownloadReq,
    metaData.count,
    msaFilter,
    previousAppState,
    stateCharterFilter,
    stateFilter,
    summaryTableTypeLookup,
    summaryType,
  ]);

  React.useEffect(() => {
    if (fetchAggDownload) {
      setFetchAggDownload(false);
      const apiPostRequest: any = downloadCerts.map(certs => {
        const splitCerts = chunkArray(certs, 1000);
        return splitCerts.map(certArray =>
          apiPost({
            filters: `${buildFilterString(
              appState.filters,
            )} AND CERT:(${certArray.join(" OR ")})`,
            search: buildSearchString(appState.search),
            agg_by: "CERT",
            agg_limit: 1000,
            agg_sum_fields: "DEPSUMBR,ASSET",
          }),
        );
      });
      const postFetch = [].concat
        .apply([], apiPostRequest)
        .map(req => fetchData(`${ApiBaseUrl()}/sod`, req));
      (async () => {
        await Promise.all(postFetch)
          .then(response => {
            const compbonedData = [].concat.apply(
              [],
              response.map(res => res.data),
            );
            const newDownloadData: any = downloadData.map(
              (dataset: KeyValue) => {
                const cert = dataset.CERT;
                const currentData: any = compbonedData.filter(
                  (data: KeyValue) => {
                    return data.CERT === cert;
                  },
                );

                return {
                  ...dataset,
                  ...currentData[0],
                  ...{ YEAR: `6/30/${dataset.YEAR}` },
                  sum_DEPSUMBR: currentData[0].sum_DEPSUMBR?.toLocaleString(),
                };
              },
            );
            setDownloadData(newDownloadData);
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {});
      })();
    }
  }, [
    appState.filters,
    appState.search,
    downloadCerts,
    downloadData,
    fetchAggDownload,
  ]);

  React.useEffect(() => {
    if (appState.ui.summaryTableLevelTwo && initialLoad) {
      updateSort("NAMEFULL", "ASC");
      if (appState.pagination.pageNumber !== 1) {
        updatePagination(1, 25);
      }
    }
    if (
      JSON.stringify(charterLevelTwoAppstate) !==
        JSON.stringify(previousAppState) ||
      initialLoad
    ) {
      initialLoad && setInitialLoad(false);
      let isMounted = true;
      setPreviousAppState(charterLevelTwoAppstate);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=YEAR:${
            appState.filters.lastYear
          }${countyInstType}${summaryTableTypeLookup[summaryType] ||
            ""}${combinedAssetSizeFilters}${stateCharterFilter}${msaFilter}${stateFilter}&limit=${rowsPerPage}&offset=${(page -
            1) *
            rowsPerPage}&sort_by=${appState.sort.sortField}&sort_order=${
            appState.sort.sortOrder
          }`,
        )
          .then(response => {
            if (isMounted) {
              const data = response.data;
              setSummaryData(data);
              setCerts(response.data.map((res: KeyValue) => res.CERT));
              setMetaData({ count: response.totals.count });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setFetchAgg(true);
          });
      })();
    }
  }, [
    appState.filters.lastYear,
    appState.sort.sortField,
    appState.sort.sortOrder,
    appState.ui.summaryTableLevelTwo,
    appState.pagination.pageNumber,
    charterLevelTwoAppstate,
    combinedAssetSizeFilters,
    countyInstType,
    initialLoad,
    page,
    previousAppState,
    msaFilter,
    rowsPerPage,
    stateCharterFilter,
    stateFilter,
    summaryTableTypeLookup,
    summaryType,
    updateSort,
    updatePagination,
  ]);

  React.useEffect(() => {
    if (certs.length && fetchAgg) {
      window.scrollTo(0, 0);
      setFetchAgg(false);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod`,
          apiPost({
            filters: `${buildFilterString(
              appState.filters,
            )} AND CERT:(${certs.join(" OR ")})`,
            search: buildSearchString(appState.search),
            agg_by: "CERT",
            agg_sum_fields: "DEPSUMBR,ASSET",
          }),
        )
          .then(response => {
            const data = summaryData.map((dataset: KeyValue) => {
              const cert = dataset.CERT;
              const currentData = response.data.filter((data: KeyValue) => {
                return data.CERT === cert;
              });

              return { ...dataset, ...currentData[0] };
            });
            setAggregatedData(data);
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
    }
  }, [
    appState,
    appState.filters,
    appState.search,
    certs,
    certs.length,
    fetchAgg,
    summaryData,
  ]);
  const headingTitle = (
    <>
      <Typography className={classes.heading}>
        {summaryTableHeader(appState.ui.reportType)} -{" "}
        {metaData.count.toLocaleString()} Matching Results
      </Typography>
      <div style={{ position: "relative", right: 0, top: 5 }}>
        <ApiDataDownload
          app="SOD"
          apiData={apiData}
          downloadData={downloadData}
        />
      </div>
    </>
  );
  return (
    <>
      {headingTitle}
      <Divider />
      {searchPills}
      {branchOfficeModal}
      <ResultPagination count={metaData.count}>
        <MultiHeaderTable
          sortProperties={sortProperties}
          tableBody={tableBody}
          tableHeaders={tableHeaders}
        />
      </ResultPagination>
    </>
  );
}
